import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  DocumentCollectionNodeData,
  DocumentRequirement,
  DocumentSet,
  InviteActorsNodeData,
} from "@gestionalo/enterprise-graph";
import type { ProductGraph } from "@/services/platformEnterprisesService";
import type { AvailableField } from "@/lib/productGraph";

interface Props {
  data: DocumentCollectionNodeData;
  onChange: (patch: Partial<DocumentCollectionNodeData>) => void;
  availableFields: AvailableField[];
  graph: ProductGraph;
  nodeId: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function DocumentCollectionEditor({
  data,
  onChange,
  availableFields,
  graph,
  nodeId: _nodeId,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const selectFields = useMemo(() => {
    const raw = availableFields.filter((f) => f.type === "select" && f.options?.length);
    const keys = new Set(raw.map((f) => f.key));
    // Per-actor fields appear as profile_0, profile_1, etc. — collapse to the
    // base key (e.g. "profile") because the node handles actor indexing itself.
    const result: AvailableField[] = [];
    const deduped = new Set<string>();
    for (const f of raw) {
      const match = f.key.match(/^(.+)_(\d+)$/);
      if (match) {
        const baseKey = match[1];
        if (keys.has(`${baseKey}_0`) && keys.has(`${baseKey}_1`)) {
          if (!deduped.has(baseKey)) {
            deduped.add(baseKey);
            result.push({ ...f, key: baseKey });
          }
          continue;
        }
      }
      result.push(f);
    }
    return result;
  }, [availableFields]);

  const selectedField = useMemo(
    () => selectFields.find((f) => f.key === data.conditionField),
    [selectFields, data.conditionField],
  );

  const handleConditionFieldChange = useCallback(
    (fieldKey: string) => {
      const field = selectFields.find((f) => f.key === fieldKey);
      if (!field?.options) {
        onChange({ conditionField: fieldKey, documentSets: {} });
        return;
      }

      // Auto-populate document sets from field options, preserving existing sets
      const newSets: Record<string, DocumentSet> = {};
      for (const opt of field.options) {
        const existing = data.documentSets[opt.value];
        newSets[opt.value] = existing ?? { label: opt.label, documents: [] };
      }
      onChange({ conditionField: fieldKey, documentSets: newSets });
    },
    [selectFields, data.documentSets, onChange],
  );

  const updateDocumentSet = useCallback(
    (conditionValue: string, documents: DocumentRequirement[]) => {
      const newSets = { ...data.documentSets };
      newSets[conditionValue] = { ...newSets[conditionValue], documents };
      onChange({ documentSets: newSets });
    },
    [data.documentSets, onChange],
  );

  const addDocument = useCallback(
    (conditionValue: string) => {
      const existing = data.documentSets[conditionValue]?.documents ?? [];
      const newDoc: DocumentRequirement = {
        key: `doc_${Date.now()}`,
        label: "",
        required: true,
      };
      updateDocumentSet(conditionValue, [...existing, newDoc]);
    },
    [data.documentSets, updateDocumentSet],
  );

  const removeDocument = useCallback(
    (conditionValue: string, index: number) => {
      const existing = data.documentSets[conditionValue]?.documents ?? [];
      updateDocumentSet(
        conditionValue,
        existing.filter((_, i) => i !== index),
      );
    },
    [data.documentSets, updateDocumentSet],
  );

  const updateDocument = useCallback(
    (conditionValue: string, index: number, patch: Partial<DocumentRequirement>) => {
      const existing = data.documentSets[conditionValue]?.documents ?? [];
      const updated = existing.map((d, i) => {
        if (i !== index) return d;
        const merged = { ...d, ...patch };
        // Auto-generate key from label if label changed
        if (patch.label != null) {
          merged.key = slugify(patch.label) || d.key;
        }
        return merged;
      });
      updateDocumentSet(conditionValue, updated);
    },
    [data.documentSets, updateDocumentSet],
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeLabelLabel")}
        </Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.nodeDescriptionLabel")}
        </Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      {/* Condition field selector */}
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.docCollectionConditionField")}
        </Label>
        <Select
          value={data.conditionField || "__none__"}
          onValueChange={(v) =>
            handleConditionFieldChange(v === "__none__" ? "" : v)
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              {t("enterpriseProductBuilder.docCollectionNoConditionField")}
            </SelectItem>
            {selectFields.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-muted-foreground">
          {t("enterpriseProductBuilder.docCollectionConditionFieldHint")}
        </p>
      </div>

      {/* Actor source */}
      <div className="space-y-1.5">
        <Label className="text-xs">
          {t("enterpriseProductBuilder.automationActorSource")}
        </Label>
        <Select
          value={data.actorSource ?? "__none__"}
          onValueChange={(v) =>
            onChange({ actorSource: v === "__none__" ? undefined : v })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              {t("enterpriseProductBuilder.automationNoActorSource")}
            </SelectItem>
            {graph.nodes
              .filter((n) => n.type === "inviteActors")
              .map((n) => {
                const inviteData = n.data as InviteActorsNodeData;
                return (
                  <SelectItem key={n.id} value={n.id}>
                    {inviteData.label || n.id}
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>
      </div>

      {/* Document sets */}
      {selectedField?.options && (
        <div className="space-y-3">
          <Label className="text-xs">
            {t("enterpriseProductBuilder.docCollectionDocuments")}
          </Label>
          {selectedField.options.map((opt) => {
            const docSet = data.documentSets[opt.value];
            const documents = docSet?.documents ?? [];
            return (
              <div
                key={opt.value}
                className="space-y-2 rounded-md border p-3"
              >
                <div className="text-xs font-medium">{opt.label}</div>
                <Input
                  className="h-7 text-xs text-muted-foreground"
                  placeholder={t("enterpriseProductBuilder.docCollectionSetDescription")}
                  value={docSet?.description ?? ""}
                  onChange={(e) => {
                    const newSets = { ...data.documentSets };
                    newSets[opt.value] = {
                      ...newSets[opt.value],
                      description: e.target.value || undefined,
                    };
                    onChange({ documentSets: newSets });
                  }}
                />
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      className="h-7 flex-1 text-xs"
                      placeholder={t(
                        "enterpriseProductBuilder.docCollectionDocumentLabel",
                      )}
                      value={doc.label}
                      onChange={(e) =>
                        updateDocument(opt.value, idx, {
                          label: e.target.value,
                        })
                      }
                    />
                    <div className="flex items-center gap-1">
                      <Checkbox
                        checked={doc.required}
                        onCheckedChange={(checked) =>
                          updateDocument(opt.value, idx, {
                            required: !!checked,
                          })
                        }
                      />
                      <span className="text-[10px] text-muted-foreground">
                        {t(
                          "enterpriseProductBuilder.docCollectionDocumentRequired",
                        )}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => removeDocument(opt.value, idx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => addDocument(opt.value)}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  {t("enterpriseProductBuilder.docCollectionAddDocument")}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {data.conditionField && !selectedField && (
        <p className="text-xs text-muted-foreground">
          {t("enterpriseProductBuilder.docCollectionNoOptions")}
        </p>
      )}
    </div>
  );
}
